/**
 * Client-side native-video gates, shared by the owner upload hooks
 * (useNativeRitualVideos, useNativeCommunityVideos).
 *
 * These mirror the backend caps (RITUAL_VIDEO_*, reused by all owners) and only
 * fail fast to avoid burning a signed URL -- the server re-checks every upload
 * and the worker re-probes duration via ffprobe.
 */

import { isFileTypeAllowed } from "@/utils/fileTypes"
import { probeVideoDuration } from "./gcsResumableUpload"

const parsePositiveInt = (raw: string | undefined, fallback: number): number => {
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

export const ALLOWED_MIME_TYPES: string[] = (
  (import.meta.env.VITE_RITUAL_VIDEO_ALLOWED_MIME as string | undefined) ??
  "video/mp4,video/quicktime,video/webm"
)
  .split(",")
  .map((s: string) => s.trim())
  .filter(Boolean)

export const MAX_BYTES = parsePositiveInt(
  import.meta.env.VITE_RITUAL_VIDEO_MAX_BYTES,
  2 * 1024 * 1024 * 1024,
)

export const MAX_DURATION_SECONDS = parsePositiveInt(
  import.meta.env.VITE_RITUAL_VIDEO_MAX_DURATION_SECONDS,
  1800,
)

export const formatBytes = (bytes: number): string => {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)}GB`
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)}KB`
  return `${bytes}B`
}

/**
 * Validate a file against the client-side caps. Returns the probed duration
 * (>0) or 0 when the probe was skipped/failed; throws a user-facing message
 * when a hard cap is exceeded.
 */
export const validateAndProbeVideo = async (file: File): Promise<number> => {
  // Extension fallback so Android's empty/octet-stream MIME for a real .mp4
  // (common from the Downloads folder) isn't wrongly rejected here.
  if (!isFileTypeAllowed(file, ALLOWED_MIME_TYPES)) {
    throw new Error(`Unsupported video format. Use ${ALLOWED_MIME_TYPES.join(", ")}.`)
  }
  if (file.size > MAX_BYTES) {
    throw new Error(`File too large (max ${formatBytes(MAX_BYTES)})`)
  }
  let duration = 0
  try {
    duration = await probeVideoDuration(file)
  } catch {
    duration = 0
  }
  if (duration > 0 && duration > MAX_DURATION_SECONDS) {
    throw new Error(`Video too long (max ${Math.floor(MAX_DURATION_SECONDS / 60)} min)`)
  }
  return duration
}
